import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import useAutoSaveForm from 'hooks/settings/useAutoSaveForm';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EDIT_PROFILE_FIELD, EDIT_PROFILE_SELECT_FIELDS } from 'utils/settings';

const getInitialValues = (user) => ({
    ...user,
    country: user?.country || null,
    timezone: user?.timezone || null,
});

const normalizeProfileValues = (values) => ({
    ...values,
    display_name: values.display_name?.trim() || '',
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
});

const getProfilePayload = (values) => ({
    profile_image: values.profile_image,
    profile_circle: values.profile_circle,
    first_name: values.first_name,
    last_name: values.last_name,
    display_name: values.display_name,
    email: values.email.trim(),
    pronoun: values.pronoun,
    english_proficiency: values.english_proficiency,
    country: values.country,
    timezone: values.timezone,
});

const useEditProfileForm = ({ onDirtyChange } = {}) => {
    const { auth, currentUser, setCurrentUser, updateProfile } = useAuth();
    const { setAccountUsers } = useApp();
    const { message } = useSnackbar();
    const { t } = useTranslation();
    const initialValues = useMemo(() => getInitialValues(currentUser), [currentUser]);
    const initializedUserId = useRef(null);

    const getErrorMessage = useCallback(
        (key, value) => {
            const normalizedValue = [EDIT_PROFILE_FIELD.FIRST_NAME, EDIT_PROFILE_FIELD.LAST_NAME].includes(key)
                ? value?.trim()
                : value;
            if (key === EDIT_PROFILE_FIELD.FIRST_NAME) {
                return normalizedValue
                    ? ''
                    : t('account-settings.profile.enter-first-name', 'Please enter your first name');
            }
            if (key === EDIT_PROFILE_FIELD.LAST_NAME)
                return normalizedValue ? '' : t('error.empty-last-name', 'Please enter your last name');
            return '';
        },
        [t]
    );
    const formSettings = useAutoSaveForm({
        fields: EDIT_PROFILE_FIELD,
        getErrorMessage,
        initialValues,
        normalize: normalizeProfileValues,
        normalizeField: (key, value) => (EDIT_PROFILE_SELECT_FIELDS.includes(key) ? value?.key || null : value),
        persist: async (values) => {
            const saved = await updateProfile(getProfilePayload(values));
            if (!saved) throw new Error('Unable to update account.');
        },
        onSuccess: (_result, values) => {
            const data = getProfilePayload(values);
            setCurrentUser((current) => ({ ...current, ...data }));
            setAccountUsers((current) => {
                if (!current) return current;
                return current.map((user) =>
                    user.owner_account_id === auth.owner_account_id ? { ...user, ...data } : user
                );
            });
            message.success(t('message.account-updated', 'Account has been updated successfully.'));
        },
        onError: () => message.error(t('message.something-went-wrong', 'Something went wrong.')),
    });
    const { isDirty } = formSettings;

    useEffect(() => {
        if (!currentUser?.id || initializedUserId.current === currentUser.id) return;
        initializedUserId.current = currentUser.id;
        formSettings.reset(getInitialValues(currentUser));
    }, [currentUser, formSettings]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    return formSettings;
};

export default useEditProfileForm;
